import { useState, useRef, useEffect } from "react";
import type { Collection, RequestItem } from '../pages/ApiRequestPage';
import { Badge } from "./ui/Badge";
import { DYNAMIC_API_URL } from '../config/api';

// Добавить пропсы для связи с RequestPanel
type SidebarProps = {
  collections: Collection[];
  setCollections: (cols: Collection[]) => void;
  newCollectionCount: number;
  setNewCollectionCount: (n: number) => void;
  openTabs: Array<{ id: string; collectionId: number; requestId: number }>;
  setOpenTabs: (tabs: Array<{ id: string; collectionId: number; requestId: number }>) => void;
  activeTabId: string | null;
  setActiveTabId: (id: string | null) => void;
  showDemo?: boolean;
  demoStep?: string;
};

export default function Sidebar({ collections, setCollections, newCollectionCount, setNewCollectionCount, openTabs, setOpenTabs, setActiveTabId, showDemo = false, demoStep }: SidebarProps) {
  // Для редактирования коллекции - упрощенная версия
  const [editingCollectionIndex, setEditingCollectionIndex] = useState<number | null>(null);
  const [editingRequestIndex, setEditingRequestIndex] = useState<{ collectionId: number; requestId: number } | null>(null);
  const newCollectionInputRef = useRef<HTMLInputElement>(null);
  const newRequestInputRef = useRef<HTMLInputElement>(null);

  // Фокусировка на инпуте при редактировании коллекции
  useEffect(() => {
    if (editingCollectionIndex !== null && newCollectionInputRef.current && !showDemo) {
      newCollectionInputRef.current.focus();
      newCollectionInputRef.current.select();
    }
  }, [editingCollectionIndex, showDemo]);

  // Фокусировка на инпуте при редактировании запроса
  useEffect(() => {
    if (editingRequestIndex !== null && newRequestInputRef.current && !showDemo) {
      newRequestInputRef.current.focus();
      newRequestInputRef.current.select();
    }
  }, [editingRequestIndex, showDemo]);

  // Сброс состояния редактирования при завершении демо
  useEffect(() => {
    if (!showDemo) {
      setEditingCollectionIndex(null);
      setEditingRequestIndex(null);
    }
  }, [showDemo]);

  // Начать редактирование коллекции
  const startEditingCollection = (index: number) => {
    setEditingCollectionIndex(index);
  };

  // Сохранить изменения в коллекции
  const saveCollectionEdit = (index: number, newName: string) => {
    if (newName.trim() !== '') {
      setCollections(collections.map((col, idx) =>
        idx === index ? { ...col, name: newName.trim() } : col
      ));
    }
    setEditingCollectionIndex(null);
  };

  // Отменить редактирование коллекции
  const cancelCollectionEdit = () => {
    setEditingCollectionIndex(null);
  };

  // Начать редактирование запроса
  const startEditingRequest = (collectionId: number, requestId: number) => {
    setEditingRequestIndex({ collectionId, requestId });
  };

  // Сохранить изменения в запросе
  const saveRequestEdit = (collectionId: number, requestId: number, newName: string) => {
    if (newName.trim() !== '') {
      editRequestInCollection(collectionId, requestId, { desc: newName.trim() });
    }
    setEditingRequestIndex(null);
  };

  // Отменить редактирование запроса
  const cancelRequestEdit = () => {
    setEditingRequestIndex(null);
  };

  // Редактировать запрос в коллекции
  const editRequestInCollection = (collectionIndex: number, requestIndex: number, updates: { method?: string; desc?: string; path?: string }) => {
    setCollections(collections.map((col, colIdx) => {
      if (colIdx === collectionIndex) {
        return {
          ...col,
          items: col.items.map((item, itemIdx) => {
            if (itemIdx === requestIndex) {
              return {
                ...item,
                ...updates
              };
            }
            return item;
          })
        };
      }
      return col;
    }));
  };

  // Удалить запрос из коллекции
  const handleDeleteRequest = (collectionId: number, requestId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем открытие таба при клике на удаление
    
    // Закрываем таб, если он открыт для этого запроса
    const tabToClose = openTabs.find(tab => 
      tab.collectionId === collectionId && tab.requestId === requestId
    );
    
    if (tabToClose) {
      const newTabs = openTabs.filter(tab => tab.id !== tabToClose.id);
      setOpenTabs(newTabs);
      
      // Если удаляемый таб был активным, переключаемся на другой
      if (newTabs.length > 0) {
        setActiveTabId(newTabs[newTabs.length - 1].id);
      } else {
        setActiveTabId(null);
      }
    }
    
    // Обновляем индексы в открытых табах для запросов после удаленного
    const updatedTabs = openTabs
      .filter(tab => !(tab.collectionId === collectionId && tab.requestId === requestId))
      .map(tab => {
        if (tab.collectionId === collectionId && tab.requestId > requestId) {
          return { ...tab, requestId: tab.requestId - 1 };
        }
        return tab;
      });
    setOpenTabs(updatedTabs);
    
    // Удаляем запрос из коллекции
    setCollections(collections.map((col, colIdx) => {
      if (colIdx === collectionId) {
        return {
          ...col,
          items: col.items.filter((_, itemIdx) => itemIdx !== requestId)
        };
      }
      return col;
    }));
  };

  const methodColors: { [key: string]: string } = {
    GET: 'text-green-700',
    POST: 'text-yellow-700',
    PUT: 'text-blue-700',
    PATCH: 'text-purple-700',
    DELETE: 'text-red-800',
    DEL: 'text-red-800', // для обратной совместимости
    HEAD: 'text-green-700',
    OPTIONS: 'text-pink-700',
  };

  // Удаляю все закомментированные и неиспользуемые функции и переменные

  // Добавление нового запроса в коллекцию
  const handleAddRequest = (colIdx: number) => {
    const host = DYNAMIC_API_URL
    const collection = collections[colIdx];
    
    // Определяем название запроса в зависимости от демо
    const requestIndex = collection.items.length;
    let newRequestName: string;
    let method: string;
    
    if (showDemo) {
      if (requestIndex === 0) {
        // Первый запрос: PUT, create-task
        newRequestName = 'create-task';
        method = 'PUT';
      } else if (requestIndex === 1) {
        // Второй запрос: POST, make-task-done
        newRequestName = 'make-task-done';
        method = 'POST';
      } else {
        newRequestName = `New Request ${requestIndex + 1}`;
        method = 'GET';
      }
    } else {
      newRequestName = `New Request ${requestIndex + 1}`;
      method = requestIndex === 0 ? 'POST' : 'GET';
    }
    
    const formattedName = newRequestName.toLowerCase().replace(/\s+/g, '-');
    
    // Добавляем префикс /demo в демо-режиме
    const demoPrefix = showDemo ? 'demo/' : '';
    
    setCollections(collections.map((col, idx) =>
      idx === colIdx
        ? { 
            ...col, 
            items: [...col.items, { 
              method: method, 
              path: `${host}/${demoPrefix}${collection.name.toLowerCase().replace(/\s+/g, '-')}/${formattedName}`, 
              desc: newRequestName 
            }] 
          }
        : col
    ));
  };

  // Открыть/создать вкладку по запросу
  const handleOpenRequestTab = (collectionId: number, requestId: number) => {
    // Проверяем, не открыт ли уже этот запрос
    const existingTab = openTabs.find(tab => 
      tab.collectionId === collectionId && tab.requestId === requestId
    );
    
    if (existingTab) {
      // Если таб уже открыт, просто переключаемся на него
      setActiveTabId(existingTab.id);
    } else {
      // Если таб не открыт, создаем новый
      const newTabId = `tab-${Date.now()}-${Math.random()}`;
      setOpenTabs([...openTabs, { id: newTabId, collectionId, requestId }]);
      setActiveTabId(newTabId);
    }
  };

  // Рендер коллекций (только валидные с именем)
  const renderCollections = () => (
    <>
      {collections.filter(col => col && col.name).map((col, collectionId) => (
        <div key={col.name + collectionId} className="mb-4 border-b border-gray-200 pb-2">
          <div className="flex items-center">
            <div
              className="flex items-center py-1 px-1 rounded select-none flex-1"
              onDoubleClick={() => startEditingCollection(collectionId)}
            >
              {editingCollectionIndex === collectionId ? (
                <input
                  ref={newCollectionInputRef}
                  type="text"
                  defaultValue={col.name}
                  onBlur={(e) => saveCollectionEdit(collectionId, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      saveCollectionEdit(collectionId, e.currentTarget.value);
                    } else if (e.key === 'Escape') {
                      cancelCollectionEdit();
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
                  autoFocus={!showDemo}
                />
              ) : (
                <span 
                  className="ml-1 font-semibold text-gray-800 cursor-pointer hover:bg-gray-200 hover:text-gray-900 transition-all duration-150 px-2 py-1 rounded"
                  style={{
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: 'rgb(33, 33, 33)',
                    cursor: 'text'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(237, 237, 237)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {col.name}
                </span>
              )}
            </div>
            {/* Плюс для добавления запроса */}
            <div className="relative">
              <button
                data-demo={(() => {
                  return demoStep === 'newEntityPlus' ? "first-request-plus" : demoStep === 'createSecondRequest' ? "second-request-plus" : "request-plus";
                })()}
                data-demo-step={collectionId === 0 ? "newEntityPlus" : "createSecondRequest"}
                className="w-7 h-7 flex items-center justify-center border border-transparent bg-transparent rounded-md transition hover:bg-gray-100 focus:outline-none focus:border-blue-500"
                style={{ 
                  fontSize: 20, 
                  fontWeight: 700, 
                  lineHeight: 1,
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title="Create new request"
                tabIndex={0}
                aria-label="Add request"
                onClick={() => {
                  handleAddRequest(collectionId);
                }}
              >
                <span 
                  className="text-gray-500 hover:text-gray-700 transition-colors" 
                  style={{ fontSize: 20, lineHeight: 1 }}
                >
                  +
                </span>
              </button>
            </div>
          </div>
          <div className="mt-1 space-y-1">
            {col.items.map(({ method, path, desc }: RequestItem, requestId: number) => {
              return (
              <div
                key={path + requestId}
                data-demo={collectionId === 0 && requestId === 0 ? "first-request" : collectionId === 0 && requestId === 1 ? "second-request" : "new-request"}
                // className={`flex items-center pl-4 gap-1 py-[2px] px-2 rounded cursor-pointer font-mono text-sm select-none transition-colors group ${
                  className={`flex items-center pl-4 gap-1 py-[2px] px-2 rounded cursor-pointer font-mono text-sm select-none transition-colors group`}
                //   isActive ? '' : 'hover:bg-gray-100'
                // } ${
                //   showDemo ? 'bg-blue-50 border border-blue-200' : ''
                // } ${
                //   isActive ? 'bg-gray-200 border border-gray-300' : ''
                // }`}
                style={{ 
                  minHeight: 28, 
                  marginBottom: 2
                }}
                onClick={() => { handleOpenRequestTab(collectionId, requestId); }}
              >
                <Badge
                  className={`block h-5 px-2 py-0 text-xs text-right cursor-pointer transition-all duration-150 ${methodColors[method] || 'text-gray-700'}`}
                  style={{ 
                    minWidth: 46,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  title="Click to change method"
                >
                  {method}
                </Badge>
                {editingRequestIndex?.collectionId === collectionId && editingRequestIndex?.requestId === requestId ? (
                  <input
                    ref={newRequestInputRef}
                    type="text"
                    defaultValue={desc || path}
                    onBlur={(e) => saveRequestEdit(collectionId, requestId, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveRequestEdit(collectionId, requestId, e.currentTarget.value);
                      } else if (e.key === 'Escape') {
                        cancelRequestEdit();
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
                    autoFocus={!showDemo}
                  />
                ) : (
                  <span 
                    className="text-gray-900 truncate max-w-[120px] ml-1 cursor-pointer transition-all duration-150 px-2 py-1 rounded" 
                    title={path}
                    style={{
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      color: 'rgb(33, 33, 33)'
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      startEditingRequest(collectionId, requestId);
                    }}
                  >
                    {desc || path}
                  </span>
                )}
                <button
                  className="ml-1 px-1 text-gray-400 opacity-0 group-hover:opacity-100 transition-all font-normal rounded"
                  title="Delete request"
                  tabIndex={-1}
                  style={{ 
                    fontSize: 16, 
                    lineHeight: 1,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={(e) => handleDeleteRequest(collectionId, requestId, e)}
                >
                  ×
                </button>
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div
      className="font-thin overflow-auto border-r border-gray-300"
      style={{
        width: '100%',
        height: '100vh',
        padding: 16,
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        color: 'black',
        background: 'white',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        boxSizing: 'border-box',
      }}
    >
      {/* Плюс для новой коллекции */}
      <div className="flex items-center mb-4 gap-2">
        <button
          data-demo="collection-plus"
          className="w-8 h-8 flex items-center justify-center border border-gray-300 bg-white rounded-md hover:bg-gray-100 transition cursor-pointer"
          style={{ fontSize: 22 }}
          title="Create new collection"
          onClick={() => {
            const newCollections = [
              ...collections,
              { name: showDemo ? 'tasks' : `New Entity ${newCollectionCount}`, items: [] }
            ];
            setCollections(newCollections);
            setNewCollectionCount(newCollectionCount + 1);
            // Начать редактирование новой коллекции только если не демо-режим
            if (!showDemo) {
              startEditingCollection(newCollections.length - 1);
            }
          }}
        >
          <span className="text-gray-500">+</span>
        </button>
        <input
          type="text"
          placeholder="Search collections"
          className="flex-1 border border-gray-200 rounded px-3 py-1 text-sm bg-white focus:outline-none focus:border-gray-400"
          style={{ height: 32 }}
        />
      </div>
      {renderCollections()}
    </div>
  );
} 
